import { useMemo, useState } from 'react'
import type { ToastInfoType } from '@trinity/utils'
import { GlobalContext, useMediaQuery, useTheme } from '../../hooks'
import { Alert, Snackbar } from '../Display'
import { Stack } from '../Layout'

interface GlobalProviderProps {
  user: TrinityAPI.LoginType
  children: React.ReactNode
}

export function GlobalProvider({ user, children }: GlobalProviderProps) {
  const [toastInfo, setToastInfo] = useState<ToastInfoType>(defaultToastInfo)
  const theme = useTheme()
  const onMobile = useMediaQuery(theme.breakpoints.between('mobile', 'tablet'))
  const onTablet = useMediaQuery(theme.breakpoints.only('tablet'))
  const onLaptop = useMediaQuery(theme.breakpoints.only('laptop'))
  const onDesktop = useMediaQuery(theme.breakpoints.up('desktop'))
  const onSkiffle = window.location.hostname.startsWith('skiffle') || window.location.host.startsWith('localhost:1236')

  const value = useMemo(
    () => ({ onMobile, onTablet, onLaptop, onDesktop, onSkiffle, user, setToastInfo }),
    [onDesktop, onLaptop, onMobile, onSkiffle, onTablet, user],
  )

  return (
    <GlobalContext.Provider value={value}>
      <Snackbar
        open={toastInfo.show && toastInfo.message !== ''}
        autoHideDuration={toastInfo.autoHideDuration === undefined ? 6000 : toastInfo.autoHideDuration}
        onClose={(_, reason) => {
          if (reason == 'clickaway') return
          setToastInfo(defaultToastInfo)
        }}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setToastInfo(defaultToastInfo)} severity={toastInfo.severity} sx={{ mt: 10 }}>
          {toastInfo.message}
          {toastInfo.child && <Stack>{toastInfo.child}</Stack>}
        </Alert>
      </Snackbar>
      {children}
    </GlobalContext.Provider>
  )
}

const defaultToastInfo: ToastInfoType = { show: false, message: '', severity: 'success' }
